import { useState } from "react";
import { leagueStandings } from "../../../mocks/leagueStandings";

function getQualificationBadge(rank: number, leagueCode: string) {
  if (leagueCode === "KLEAGUE") {
    if (rank <= 3) return { text: "ACL", color: "bg-brand-orange" };
    return null;
  }
  if (rank <= 4) return { text: "챔스", color: "bg-brand-orange" };
  if (rank <= 6) return { text: "유로파", color: "bg-emerald-500" };
  if (rank === 7) return { text: "컨퍼런스", color: "bg-teal-500" };
  return null;
}

export default function LeagueStandings() {
  const [activeLeague, setActiveLeague] = useState(0);
  const current = leagueStandings[activeLeague];

  return (
    <section className="w-full bg-dark-bg py-16 md:py-24">
      <div className="w-full px-4 md:px-8">
        {/* Header */}
        <div className="max-w-5xl mx-auto mb-10">
          <h2 className="text-white font-bold text-2xl md:text-3xl mb-2">
            주요 리그 순위표
          </h2>
          <p className="text-dark-text-muted text-sm md:text-base">
            실시간 주요 리그 순위를 한눈에 확인하세요
          </p>
        </div>

        {/* League Tabs */}
        <div className="max-w-5xl mx-auto mb-8">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {leagueStandings.map((league, idx) => (
              <button
                key={league.league}
                onClick={() => setActiveLeague(idx)}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer flex-shrink-0 ${
                  idx === activeLeague
                    ? "bg-brand-orange text-white"
                    : "bg-dark-surface text-dark-text-muted hover:text-white hover:bg-dark-border"
                }`}
              >
                {league.name}
              </button>
            ))}
          </div>
        </div>

        {/* Standings Table */}
        <div className="max-w-5xl mx-auto">
          <div className="bg-dark-surface rounded-xl border border-dark-border overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-[48px_1fr_48px_48px_48px_48px_48px_48px] md:grid-cols-[56px_1fr_64px_56px_56px_56px_56px_56px] items-center px-4 py-3 border-b border-dark-border bg-dark-bg/50">
              <span className="text-dark-text-muted text-xs md:text-sm font-medium text-center">순위</span>
              <span className="text-dark-text-muted text-xs md:text-sm font-medium pl-2">팀</span>
              <span className="text-dark-text-muted text-xs md:text-sm font-medium text-center">경기</span>
              <span className="text-dark-text-muted text-xs md:text-sm font-medium text-center hidden sm:block">승</span>
              <span className="text-dark-text-muted text-xs md:text-sm font-medium text-center hidden sm:block">무</span>
              <span className="text-dark-text-muted text-xs md:text-sm font-medium text-center hidden sm:block">패</span>
              <span className="text-dark-text-muted text-xs md:text-sm font-medium text-center">승점</span>
              <span className="text-dark-text-muted text-xs md:text-sm font-medium text-center hidden sm:block">득실차</span>
            </div>

            {/* Table Rows */}
            {current.standings.map((team) => {
              const isTop1 = team.rank === 1;
              const isTop4 = team.rank >= 2 && team.rank <= 4;
              const isTop = isTop1 || isTop4;
              const badge = getQualificationBadge(team.rank, current.league);
              const teamColor = team.color || "#666666";
              const teamBadge = team.badge || "";

              return (
                <div
                  key={team.rank}
                  className={`grid grid-cols-[48px_1fr_48px_48px_48px_48px_48px_48px] md:grid-cols-[56px_1fr_64px_56px_56px_56px_56px_56px] items-center px-4 py-3 border-b border-dark-border/50 transition-colors hover:bg-dark-border/30 ${
                    isTop ? "bg-dark-border/20" : ""
                  }`}
                >
                  {/* Rank */}
                  <div className="flex justify-center">
                    <span
                      className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs md:text-sm font-bold ${
                        isTop1
                          ? "bg-brand-orange text-white"
                          : isTop4
                          ? "bg-dark-border text-white"
                          : "text-dark-text-muted"
                      }`}
                    >
                      {team.rank}
                    </span>
                  </div>

                  {/* Team Logo + Name */}
                  <div className="flex items-center gap-3 pl-2 min-w-0">
                    {/* Team Logo Badge */}
                    <div className="relative w-8 h-8 md:w-9 md:h-9 flex-shrink-0">
                      {team.logo && (
                        <img
                          src={team.logo}
                          alt={team.team}
                          className="absolute inset-0 w-full h-full rounded-full object-contain bg-white z-10"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      )}
                      <span
                        className="absolute inset-0 w-full h-full rounded-full flex items-center justify-center text-[10px] md:text-[11px] font-extrabold text-white border-2 border-white/20"
                        style={{ backgroundColor: teamColor }}
                      >
                        {teamBadge}
                      </span>
                    </div>
                    <span className={`text-sm md:text-base font-medium truncate ${isTop1 ? "text-brand-orange" : "text-white"}`}>
                      {team.team}
                    </span>
                    {badge && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold text-white flex-shrink-0 hidden sm:inline-block ${badge.color}`}>
                        {badge.text}
                      </span>
                    )}
                  </div>

                  {/* Played */}
                  <span className="text-dark-text-muted text-xs md:text-sm text-center">{team.played}</span>

                  {/* Win */}
                  <span className="text-dark-text-muted text-xs md:text-sm text-center hidden sm:block">{team.win}</span>

                  {/* Draw */}
                  <span className="text-dark-text-muted text-xs md:text-sm text-center hidden sm:block">{team.draw}</span>

                  {/* Loss */}
                  <span className="text-dark-text-muted text-xs md:text-sm text-center hidden sm:block">{team.loss}</span>

                  {/* Points */}
                  <span className={`text-xs md:text-sm font-bold text-center ${isTop1 ? "text-brand-orange" : "text-white"}`}>
                    {team.pts}
                  </span>

                  {/* Goal Difference */}
                  <span className={`text-xs md:text-sm text-center hidden sm:block ${team.gd > 0 ? "text-green-400" : team.gd < 0 ? "text-red-400" : "text-dark-text-muted"}`}>
                    {team.gd > 0 ? `+${team.gd}` : team.gd}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Season info */}
          <p className="text-dark-text-muted text-xs mt-4 text-center">
            {current.name} {current.season} 시즌 기준
          </p>
        </div>
      </div>
    </section>
  );
}